import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../config';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    const success = searchParams.get('success');
    const code = searchParams.get('code');

    // Check if authentication was successful
    if (success !== 'true') {
      console.error('❌ Authentication failed - no success param');
      navigate('/login?error=Authentication failed');
      return;
    }

    // SECURITY: Clear the code from the URL immediately to prevent leakage via history/referer
    if (code) {
      window.history.replaceState({}, '', '/auth/callback');
    }

    // Try cookie-based auth first, fall back to code exchange for browsers that block third-party cookies
    fetchUserProfile(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  const fetchUserProfile = async (code) => {
    // A fresh Google login must start at the default QA team, exactly like
    // AuthContext login()/register(): drop any acting-team this tab still holds
    // from a previous user (per-tab sessionStorage, ACTING_TEAM_KEY in
    // constants/qaTeams.js). A stale 'kick'/'hosts' would be forwarded as
    // X-QA-Team and the backend 403s that header for non-admin roles, killing
    // every QA request — including /api/qa/check-access. Cleared once here, up
    // front, so it covers all three setUser paths below (cookie auth with and
    // without a token in the payload, and the code-exchange fallback).
    try { sessionStorage.removeItem('clara_qa_acting_team'); } catch { /* sessionStorage unavailable */ }

    // First, try cookie-based authentication (works for most browsers)
    try {
      const response = await axios.get(`${API_URL}/api/auth/profile`, {
        withCredentials: true
      });

      // Cookie auth succeeded
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        const { token, ...userData } = response.data;
        setUser(userData);
      } else {
        setUser(response.data);
      }

      if (response.data._id) {
        localStorage.setItem('userId', response.data._id);
      }

      await redirectToWorkspace( response.data.token);
      return;
    } catch (cookieError) {
      // Cookie auth failed (likely Brave or other privacy-focused browser)
      console.warn('Cookie auth failed, trying code exchange fallback:', cookieError.message);

      // Fall back to one-time code exchange if available
      if (code) {
        try {
          // Exchange the one-time code for the access token
          const exchangeResponse = await axios.post(
            `${API_URL}/api/auth/exchange-code`,
            { code },
            { withCredentials: true }
          );

          const accessToken = exchangeResponse.data.token;
          localStorage.setItem('token', accessToken);

          // Fetch profile using the new token
          const response = await axios.get(`${API_URL}/api/auth/profile`, {
            headers: { Authorization: `Bearer ${accessToken}` }
          });

          const { token, ...userData } = response.data;
          setUser(userData);

          if (response.data._id) {
            localStorage.setItem('userId', response.data._id);
          }

          await redirectToWorkspace( accessToken);
          return;
        } catch (codeError) {
          console.error('❌ Code exchange also failed:', codeError);
          navigate('/login?error=Authentication failed');
          return;
        }
      }

      // No fallback available
      console.error('❌ Error fetching user profile:', cookieError);
      navigate('/login?error=Failed to fetch user profile');
    }
  };

  const redirectToWorkspace = async (token) => {
    navigate('/home');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-primary border-r-transparent"></div>
        <p className="mt-4 text-foreground">Processing authentication...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
